import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Upload, Trash2, Edit2, Eye, EyeOff, Save, X, 
  Image, FileCode, Loader2, Check, AlertTriangle, 
  ArrowLeft, Download, Users, BarChart2, Tag, Percent,
  Calendar, Hash, ToggleLeft, ToggleRight, Flame, Sun, Snowflake,
  ChevronRight, Activity, Clock, Target, TrendingUp, MessageSquare
} from 'lucide-react';
import WhatsAppAdmin from '../components/WhatsAppAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminLabPage({ embeddedCredentials = null, embeddedTab = null }) {
  const embedded = Boolean(embeddedCredentials);
  const [isAuthenticated, setIsAuthenticated] = useState(embedded);
  const [authError, setAuthError] = useState('');
  const [credentials, setCredentials] = useState(embeddedCredentials || { username: '', password: '' });
  
  const [strategies, setStrategies] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadStats, setLeadStats] = useState({ hot: 0, warm: 0, cold: 0, tester: 0 });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(embeddedTab || 'strategies'); // strategies | leads | whatsapp
  
  // Lead filter and detail state
  const [leadTierFilter, setLeadTierFilter] = useState('all'); // all | HOT | WARM | COLD | tester
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadActivities, setLeadActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    instrument: ''
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  
  // File upload state
  const [uploadingBanner, setUploadingBanner] = useState(null);
  const [uploadingScript, setUploadingScript] = useState(null);
  
  const bannerInputRef = useRef(null);
  const scriptInputRef = useRef(null);

  // Script viewer state
  const [viewingScript, setViewingScript] = useState(null);
  const [scriptContent, setScriptContent] = useState(null);
  const [loadingScript, setLoadingScript] = useState(false);

  // Auth headers
  const getAuthHeaders = () => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return {
      'Authorization': `Basic ${auth}`,
      'Content-Type': 'application/json'
    };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        setIsAuthenticated(true);
        fetchStrategies();
        fetchLeads();
      } else {
        setAuthError('Invalid credentials');
      }
    } catch (err) {
      setAuthError('Connection error');
    }
  };

  const fetchStrategies = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/strategies`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setStrategies(data.strategies || []);
      }
    } catch (err) {
      console.error('Failed to fetch strategies:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/leads`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
        setLeadStats(data.stats || { hot: 0, warm: 0, cold: 0, tester: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    }
  };

  const fetchLeadActivities = async (leadId) => {
    setLoadingActivities(true);
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/leads/${leadId}/activities`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setLeadActivities(data.activities || []);
      }
    } catch (err) {
      console.error('Failed to fetch activities:', err);
    } finally {
      setLoadingActivities(false);
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/leads/${leadId}/status?status=${status}`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        // Update local state
        setLeads(leads.map(l => l.id === leadId ? { ...l, status } : l));
        if (selectedLead?.id === leadId) {
          setSelectedLead({ ...selectedLead, status });
        }
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: editingStrategy ? prev.slug : generateSlug(name)
    }));
  };

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.slug) {
      setFormError('Name and slug are required');
      return;
    }
    
    setSaving(true);
    setFormError('');
    
    try {
      const url = editingStrategy 
        ? `${API_URL}/api/strategy-lab/admin/strategies/${editingStrategy.slug}`
        : `${API_URL}/api/strategy-lab/admin/strategies`;
      
      const method = editingStrategy ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setShowForm(false);
        setEditingStrategy(null);
        setFormData({ name: '', slug: '', description: '', instrument: '' });
        fetchStrategies();
      } else {
        const data = await res.json();
        setFormError(data.detail || 'Failed to save strategy');
      }
    } catch (err) {
      setFormError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (strategy) => {
    setEditingStrategy(strategy);
    setFormData({
      name: strategy.name || '',
      slug: strategy.slug || '',
      description: strategy.description || '',
      instrument: strategy.instrument || ''
    });
    setShowForm(true);
    setFormError('');
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this strategy?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/strategies/${slug}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        fetchStrategies();
      }
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleToggleStatus = async (strategy) => {
    const newStatus = strategy.status === 'published' ? 'draft' : 'published';
    
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/strategies/${strategy.slug}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        fetchStrategies();
      }
    } catch (err) {
      console.error('Failed to toggle status:', err);
    }
  };

  const handleBannerUpload = async (e, slug) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingBanner(slug);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/strategies/${slug}/upload-banner`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${auth}` },
        body: formData
      });
      
      if (res.ok) {
        fetchStrategies();
      }
    } catch (err) {
      console.error('Failed to upload banner:', err);
    } finally {
      setUploadingBanner(null);
    }
  };

  const handleScriptUpload = async (e, slug) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingScript(slug);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/strategies/${slug}/upload-script`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${auth}` },
        body: formData
      });
      
      if (res.ok) {
        fetchStrategies();
      }
    } catch (err) {
      console.error('Failed to upload script:', err);
    } finally {
      setUploadingScript(null);
    }
  };

  // View script content
  const handleViewScript = async (strategy) => {
    setViewingScript(strategy);
    setLoadingScript(true);
    setScriptContent(null);
    
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/strategies/${strategy.slug}/view-script`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setScriptContent(data);
      } else {
        const errData = await res.json();
        setScriptContent({ error: errData.detail || 'Failed to load script' });
      }
    } catch (err) {
      console.error('Failed to view script:', err);
      setScriptContent({ error: 'Network error. Please try again.' });
    } finally {
      setLoadingScript(false);
    }
  };

  const closeScriptViewer = () => {
    setViewingScript(null);
    setScriptContent(null);
  };

  // Inside /admin/crm: already signed in there
  useEffect(() => {
    if (embedded) {
      fetchStrategies();
      fetchLeads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <h1 className="font-heading font-bold text-2xl text-white mb-2">Strategy Lab Admin</h1>
            <p className="text-white/50 text-sm">Enter your credentials to continue</p>
          </div>
          
          <form onSubmit={handleLogin} className="glass rounded-2xl p-6 space-y-4">
            <div>
              <label className="text-xs text-white/50 mb-1.5 block">Username</label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                placeholder="admin"
                required
              />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1.5 block">Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                placeholder="••••••••"
                required
              />
            </div>
            
            {authError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertTriangle size={16} className="text-red-400" />
                <span className="text-sm text-red-400">{authError}</span>
              </div>
            )}
            
            <button
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-[#8B5CF6] to-[#007AFF] rounded-xl text-white font-semibold transition-opacity hover:opacity-90"
            >
              Login
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-[#050508] pt-20 pb-12'}>
      <div className={embedded ? '' : 'max-w-6xl mx-auto px-6'}>
        {/* Header */}
        <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${embedded ? 'mb-4' : 'mb-8'}`}>
          <div>
            <h1 className={embedded ? 'font-heading font-semibold text-lg text-white' : 'font-heading font-bold text-2xl text-white'}>{embedded && activeTab === 'whatsapp' ? 'WhatsApp' : 'Strategy Lab Admin'}</h1>
            <p className="text-white/50 text-sm mt-1">{embedded && activeTab === 'whatsapp' ? 'Send WhatsApp messages to leads' : 'Manage your trading strategies'}</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Tabs */}
            <div className={embedded ? 'hidden' : 'flex bg-white/5 rounded-lg p-1'}>
              <button
                onClick={() => setActiveTab('strategies')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'strategies' ? 'bg-[#8B5CF6] text-white' : 'text-white/60 hover:text-white'}`}
              >
                Strategies
              </button>
              <button
                onClick={() => { setActiveTab('leads'); fetchLeads(); }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'leads' ? 'bg-[#8B5CF6] text-white' : 'text-white/60 hover:text-white'}`}
              >
                Leads ({leads.length})
              </button>
              <button
                onClick={() => { setActiveTab('whatsapp'); fetchLeads(); }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'whatsapp' ? 'bg-[#25D366] text-white' : 'text-white/60 hover:text-white'}`}
                data-testid="whatsapp-tab"
              >
                <span className="flex items-center gap-1.5">
                  <MessageSquare size={14} />
                  WhatsApp
                </span>
              </button>
            </div>
            
            {activeTab === 'strategies' && (
              <button
                onClick={() => { setShowForm(true); setEditingStrategy(null); setFormData({ name: '', slug: '', description: '', instrument: '' }); }}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#8B5CF6] to-[#007AFF] rounded-xl text-white text-sm font-medium"
              >
                <Plus size={16} />
                Add Strategy
              </button>
            )}
            
          </div>
        </div>

        {/* Strategies Tab */}
        {activeTab === 'strategies' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-[#8B5CF6] animate-spin" />
              </div>
            ) : strategies.length === 0 ? (
              <div className="text-center py-20 glass rounded-2xl">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <FileCode className="w-8 h-8 text-white/20" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">No strategies yet</h3>
                <p className="text-white/50 text-sm mb-6">Add your first trading strategy</p>
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#8B5CF6]/20 hover:bg-[#8B5CF6]/30 border border-[#8B5CF6]/30 rounded-xl text-[#8B5CF6] text-sm font-medium transition-colors"
                >
                  <Plus size={16} />
                  Add Strategy
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {strategies.map((strategy) => (
                  <motion.div
                    key={strategy.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass rounded-xl p-5"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Banner Preview */}
                      <div className="w-full lg:w-48 h-28 rounded-lg bg-white/5 border border-white/10 overflow-hidden flex-shrink-0 relative group">
                        {strategy.banner_image ? (
                          <img
                            src={strategy.banner_image.startsWith('monkalgo/') 
                              ? `${API_URL}/api/strategy-lab/files/${strategy.banner_image}`
                              : `${API_URL}/api/strategy-lab/images/${strategy.banner_image}`}
                            alt={strategy.name}
                            className="w-full h-full object-cover"
                            key={strategy.banner_image}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-8 h-8 text-white/20" />
                          </div>
                        )}
                        <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                          {uploadingBanner === strategy.slug ? (
                            <Loader2 className="w-6 h-6 text-white animate-spin" />
                          ) : (
                            <>
                              <Upload className="w-5 h-5 text-white" />
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleBannerUpload(e, strategy.slug)}
                              />
                            </>
                          )}
                        </label>
                      </div>

                      {/* Strategy Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-heading font-semibold text-white text-lg">{strategy.name}</h3>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${strategy.status === 'published' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-white/10 text-white/50'}`}>
                                {strategy.status === 'published' ? 'Published' : 'Draft'}
                              </span>
                            </div>
                            <p className="text-white/40 text-xs mb-2">/{strategy.slug}</p>
                            <p className="text-white/60 text-sm line-clamp-2">{strategy.description || 'No description'}</p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 mt-3">
                          {strategy.instrument && (
                            <span className="px-2.5 py-1 rounded-lg bg-[#8B5CF6]/10 text-[#8B5CF6] text-xs font-medium">
                              {strategy.instrument}
                            </span>
                          )}
                          <span className="text-white/30 text-xs flex items-center gap-1">
                            <Eye size={12} /> {strategy.view_count || 0} views
                          </span>
                          <span className="text-white/30 text-xs flex items-center gap-1">
                            <Download size={12} /> {strategy.download_count || 0} downloads
                          </span>
                          {strategy.script_file ? (
                            <span 
                              className="text-[#10B981] text-xs flex items-center gap-1 cursor-pointer hover:text-[#10B981]/80"
                              onClick={() => handleViewScript(strategy)}
                              title={`View: ${strategy.script_original_filename || 'Script'}`}
                            >
                              <Check size={12} /> 
                              <span className="max-w-[120px] truncate">
                                {strategy.script_original_filename || 'Script uploaded'}
                              </span>
                              <Eye size={10} className="ml-0.5" />
                            </span>
                          ) : (
                            <span className="text-[#F59E0B] text-xs flex items-center gap-1">
                              <AlertTriangle size={12} /> No script
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* View Script Button (only if script exists) */}
                        {strategy.script_file && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewScript(strategy); }}
                            data-testid={`view-script-${strategy.slug}`}
                            className="flex items-center gap-1.5 px-3 py-2 bg-[#10B981]/10 hover:bg-[#10B981]/20 border border-[#10B981]/30 rounded-lg text-[#10B981] text-xs font-medium transition-colors"
                            title="View Script Code"
                          >
                            <Eye size={14} />
                            View
                          </button>
                        )}

                        {/* Upload Script */}
                        <label className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white/70 text-xs font-medium cursor-pointer transition-colors">
                          {uploadingScript === strategy.slug ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <FileCode size={14} />
                          )}
                          Script
                          <input
                            type="file"
                            accept=".pine,.mq5,.mq4,.txt"
                            className="hidden"
                            onChange={(e) => handleScriptUpload(e, strategy.slug)}
                          />
                        </label>

                        {/* Toggle Status */}
                        <button
                          onClick={() => handleToggleStatus(strategy)}
                          className={`p-2 rounded-lg border transition-colors ${strategy.status === 'published' ? 'bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]' : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}
                          title={strategy.status === 'published' ? 'Unpublish' : 'Publish'}
                        >
                          {strategy.status === 'published' ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>

                        {/* Edit */}
                        <button
                          onClick={() => handleEdit(strategy)}
                          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(strategy.slug)}
                          className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Leads Tab - Deduplicated CRM */}
        {activeTab === 'leads' && (
          <div className="flex gap-4">
            {/* Main Leads Panel */}
            <div className={`glass rounded-2xl overflow-hidden flex-1 ${selectedLead ? 'lg:flex-[2]' : ''}`}>
              {/* Header with Stats */}
              <div className="p-4 border-b border-white/10">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="font-heading font-semibold text-white">
                      Leads (unique): {leads.length}
                    </h3>
                    <p className="text-white/40 text-xs mt-1">
                      🔥 Hot: {leadStats.hot} • 🌤️ Warm: {leadStats.warm} • ❄️ Cold: {leadStats.cold}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Export CSV */}
                    {leads.length > 0 && (
                      <button
                        onClick={() => {
                          const filteredLeads = leadTierFilter === 'all' ? leads 
                            : leadTierFilter === 'tester' ? leads.filter(l => l.tester_count > 0)
                            : leads.filter(l => l.tier === leadTierFilter);
                          
                          const headers = ['Name', 'All Emails', 'All Phones', '#Pines', 'Tester', 'Score', 'Tier', 'Status', 'First Seen', 'Last Seen'];
                          const csvContent = [
                            headers.join(','),
                            ...filteredLeads.map(lead => [
                              `"${(lead.name || '').replace(/"/g, '""')}"`,
                              `"${(lead.emails || [lead.email_raw || lead.email]).filter(Boolean).join('; ').replace(/"/g, '""')}"`,
                              `"${(lead.phones || [lead.phone_raw || lead.phone]).filter(Boolean).join('; ').replace(/"/g, '""')}"`,
                              lead.pine_count || 0,
                              lead.tester_count > 0 ? 'Yes' : 'No',
                              lead.score || 0,
                              lead.tier || 'COLD',
                              lead.status || 'New',
                              lead.first_seen ? new Date(lead.first_seen).toLocaleDateString() : '',
                              lead.last_seen ? new Date(lead.last_seen).toLocaleDateString() : ''
                            ].join(','))
                          ].join('\n');
                          
                          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `leads-${leadTierFilter}-${new Date().toISOString().split('T')[0]}.csv`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 bg-[#10B981]/10 hover:bg-[#10B981]/20 border border-[#10B981]/30 rounded-lg text-[#10B981] text-xs font-medium transition-colors"
                      >
                        <Download size={14} />
                        Export CSV
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Tier Filter Chips */}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setLeadTierFilter('all')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      leadTierFilter === 'all' 
                        ? 'bg-[#8B5CF6] text-white' 
                        : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    All ({leads.length})
                  </button>
                  <button
                    onClick={() => setLeadTierFilter('HOT')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
                      leadTierFilter === 'HOT' 
                        ? 'bg-red-500 text-white' 
                        : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Flame size={12} /> Hot ({leadStats.hot})
                  </button>
                  <button
                    onClick={() => setLeadTierFilter('WARM')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
                      leadTierFilter === 'WARM' 
                        ? 'bg-amber-500 text-white' 
                        : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Sun size={12} /> Warm ({leadStats.warm})
                  </button>
                  <button
                    onClick={() => setLeadTierFilter('COLD')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
                      leadTierFilter === 'COLD' 
                        ? 'bg-slate-500 text-white' 
                        : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Snowflake size={12} /> Cold ({leadStats.cold})
                  </button>
                  <button
                    onClick={() => setLeadTierFilter('tester')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
                      leadTierFilter === 'tester' 
                        ? 'bg-[#F59E0B] text-white' 
                        : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Target size={12} /> Tester ({leadStats.tester})
                  </button>
                </div>
              </div>
              
              {/* Leads Table */}
              {(() => {
                const filteredLeads = leadTierFilter === 'all' ? leads 
                  : leadTierFilter === 'tester' ? leads.filter(l => l.tester_count > 0)
                  : leads.filter(l => l.tier === leadTierFilter);
                
                return filteredLeads.length === 0 ? (
                  <div className="text-center py-16">
                    <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/50">No leads found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/5">
                          <th className="text-left px-4 py-3 text-xs text-white/40 font-medium">Name</th>
                          <th className="text-left px-4 py-3 text-xs text-white/40 font-medium">Email</th>
                          <th className="text-left px-4 py-3 text-xs text-white/40 font-medium">Phone</th>
                          <th className="text-center px-4 py-3 text-xs text-white/40 font-medium">#Pines</th>
                          <th className="text-center px-4 py-3 text-xs text-white/40 font-medium">Tester</th>
                          <th className="text-center px-4 py-3 text-xs text-white/40 font-medium">Score</th>
                          <th className="text-center px-4 py-3 text-xs text-white/40 font-medium">Tier</th>
                          <th className="text-left px-4 py-3 text-xs text-white/40 font-medium">Last Activity</th>
                          <th className="text-left px-4 py-3 text-xs text-white/40 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLeads.map((lead) => (
                          <tr 
                            key={lead.id} 
                            onClick={() => { setSelectedLead(lead); fetchLeadActivities(lead.id); }}
                            className={`border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${selectedLead?.id === lead.id ? 'bg-white/10' : ''}`}
                          >
                            <td className="px-4 py-3 text-sm text-white font-medium">{lead.name}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className="text-white/70">{(lead.emails || [])[0] || lead.email_raw || lead.email}</span>
                              {(lead.emails || []).length > 1 && (
                                <span className="ml-1 text-[10px] text-[#8B5CF6]">+{lead.emails.length - 1}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className="text-white/70">{(lead.phones || [])[0] || lead.phone_raw || lead.phone || '-'}</span>
                              {(lead.phones || []).length > 1 && (
                                <span className="ml-1 text-[10px] text-[#8B5CF6]">+{lead.phones.length - 1}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-center text-[#8B5CF6] font-medium">{lead.pine_count || 0}</td>
                            <td className="px-4 py-3 text-center">
                              {lead.tester_count > 0 ? (
                                <Check size={16} className="text-[#10B981] mx-auto" />
                              ) : (
                                <span className="text-white/30">–</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-bold ${
                                lead.score >= 50 ? 'text-red-400' : 
                                lead.score >= 20 ? 'text-amber-400' : 'text-slate-400'
                              }`}>{lead.score || 0}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {lead.tier === 'HOT' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs font-medium">
                                  <Flame size={10} /> HOT
                                </span>
                              ) : lead.tier === 'WARM' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">
                                  <Sun size={10} /> WARM
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-400 text-xs font-medium">
                                  <Snowflake size={10} /> COLD
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-white/50">
                              {lead.last_seen ? new Date(lead.last_seen).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <select
                                value={lead.status || 'New'}
                                onChange={(e) => { e.stopPropagation(); updateLeadStatus(lead.id, e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#8B5CF6]/50"
                              >
                                <option value="New">New</option>
                                <option value="Contacted">Contacted</option>
                                <option value="Qualified">Qualified</option>
                                <option value="Converted">Converted</option>
                                <option value="Lost">Lost</option>
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
            
            {/* Lead Detail Panel */}
            <AnimatePresence>
              {selectedLead && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="hidden lg:block w-96 glass rounded-2xl overflow-hidden flex-shrink-0"
                >
                  {/* Header */}
                  <div className="p-4 border-b border-white/10 bg-gradient-to-r from-[#8B5CF6]/10 to-[#06b6d4]/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-heading font-semibold text-white">{selectedLead.name}</h4>
                      <button
                        onClick={() => setSelectedLead(null)}
                        className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    
                    {/* All Emails */}
                    <div className="mb-2">
                      <p className="text-white/40 text-xs mb-1">Emails:</p>
                      {(selectedLead.emails || [selectedLead.email_raw || selectedLead.email]).filter(Boolean).map((email, idx) => (
                        <p key={idx} className="text-white/70 text-sm">{email}</p>
                      ))}
                    </div>
                    
                    {/* All Phones */}
                    <div>
                      <p className="text-white/40 text-xs mb-1">Phones:</p>
                      {(selectedLead.phones || [selectedLead.phone_raw || selectedLead.phone]).filter(Boolean).map((phone, idx) => (
                        <p key={idx} className="text-white/60 text-sm">{phone}</p>
                      ))}
                    </div>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 mt-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#8B5CF6]">{selectedLead.score || 0}</div>
                        <div className="text-xs text-white/40">Score</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-white">{selectedLead.pine_count || 0}</div>
                        <div className="text-xs text-white/40">Pine Scripts</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#F59E0B]">{selectedLead.tester_count || 0}</div>
                        <div className="text-xs text-white/40">Tester</div>
                      </div>
                      <div className="text-center">
                        {selectedLead.tier === 'HOT' ? (
                          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                            <Flame size={20} className="text-red-400" />
                          </div>
                        ) : selectedLead.tier === 'WARM' ? (
                          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                            <Sun size={20} className="text-amber-400" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-slate-500/20 flex items-center justify-center">
                            <Snowflake size={20} className="text-slate-400" />
                          </div>
                        )}
                        <div className="text-xs text-white/40 mt-1">{selectedLead.tier}</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Activity Timeline */}
                  <div className="p-4">
                    <h5 className="text-xs text-white/40 font-medium uppercase tracking-wide mb-3 flex items-center gap-2">
                      <Activity size={12} /> Activity Timeline
                    </h5>
                    
                    {loadingActivities ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 size={20} className="animate-spin text-white/40" />
                      </div>
                    ) : leadActivities.length === 0 ? (
                      <p className="text-white/40 text-sm text-center py-8">No activities recorded</p>
                    ) : (
                      <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {leadActivities.map((activity) => (
                          <div key={activity.id} className="flex gap-3">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              activity.type === 'tester' ? 'bg-[#F59E0B]/20' : 'bg-[#8B5CF6]/20'
                            }`}>
                              {activity.type === 'tester' ? (
                                <Target size={14} className="text-[#F59E0B]" />
                              ) : (
                                <FileCode size={14} className="text-[#8B5CF6]" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white">
                                {activity.type === 'tester' ? 'Downloaded Tester' : 'Downloaded Pine Script'}
                              </p>
                              <p className="text-xs text-white/50 truncate">{activity.strategy_name}</p>
                              <p className="text-xs text-white/30 flex items-center gap-1 mt-1">
                                <Clock size={10} />
                                {activity.created_at ? new Date(activity.created_at).toLocaleString() : '-'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Timestamps */}
                  <div className="p-4 border-t border-white/10 bg-white/5">
                    <div className="flex justify-between text-xs text-white/40">
                      <span>First seen: {selectedLead.first_seen ? new Date(selectedLead.first_seen).toLocaleDateString() : '-'}</span>
                      <span>Last seen: {selectedLead.last_seen ? new Date(selectedLead.last_seen).toLocaleDateString() : '-'}</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* WhatsApp Tab */}
        {activeTab === 'whatsapp' && (
          <WhatsAppAdmin credentials={credentials} leads={leads} />
        )}

      </div>

      {/* Create/Edit Form Modal */}
      <AnimatePresence>
        {showForm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setShowForm(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-lg bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl"
            >
              <div className="p-6 border-b border-white/10">
                <button
                  onClick={() => setShowForm(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"
                >
                  <X size={16} className="text-white/60" />
                </button>
                <h2 className="font-heading font-bold text-xl text-white">
                  {editingStrategy ? 'Edit Strategy' : 'Add New Strategy'}
                </h2>
              </div>

              <form onSubmit={handleCreateOrUpdate} className="p-6 space-y-4">
                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Strategy Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                    placeholder="ICT Silver Bullet"
                    required
                  />
                </div>

                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50 font-mono text-sm"
                    placeholder="ict-silver-bullet"
                    required
                    disabled={!!editingStrategy}
                  />
                  <p className="text-[10px] text-white/30 mt-1">URL: /strategy-lab/{formData.slug || 'your-slug'}</p>
                </div>

                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50 resize-none"
                    placeholder="A brief description of the strategy..."
                  />
                </div>

                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Instrument (optional)</label>
                  <select
                    value={formData.instrument}
                    onChange={(e) => setFormData(prev => ({ ...prev, instrument: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#8B5CF6]/50"
                  >
                    <option value="">Select instrument...</option>
                    <option value="Gold (XAUUSD)">Gold (XAUUSD)</option>
                    <option value="Silver (XAGUSD)">Silver (XAGUSD)</option>
                    <option value="Nifty 50">Nifty 50</option>
                    <option value="Bank Nifty">Bank Nifty</option>
                    <option value="NAS100">NAS100</option>
                    <option value="US30">US30</option>
                    <option value="SPX500">SPX500</option>
                    <option value="EUR/USD">EUR/USD</option>
                    <option value="GBP/USD">GBP/USD</option>
                    <option value="Crypto">Crypto</option>
                    <option value="Multiple">Multiple Instruments</option>
                  </select>
                </div>

                {formError && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <AlertTriangle size={16} className="text-red-400" />
                    <span className="text-sm text-red-400">{formError}</span>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 py-3 bg-gradient-to-r from-[#8B5CF6] to-[#007AFF] rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-70"
                  >
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                    {saving ? 'Saving...' : (editingStrategy ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
        
        {/* Script Viewer Modal */}
        {viewingScript && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={closeScriptViewer}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-4xl max-h-[85vh] bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="p-4 border-b border-white/10 bg-gradient-to-r from-[#10B981]/10 to-[#06b6d4]/10 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
                    <FileCode size={20} className="text-[#10B981]" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-white">
                      {viewingScript.name}
                    </h2>
                    <p className="text-white/50 text-xs">
                      {scriptContent?.original_filename || 'Loading...'}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={closeScriptViewer}
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto p-4">
                {loadingScript ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 size={32} className="animate-spin text-[#10B981]" />
                    <span className="ml-3 text-white/60">Loading script...</span>
                  </div>
                ) : scriptContent?.error ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <AlertTriangle size={48} className="text-red-400 mb-4" />
                    <p className="text-red-400 text-lg font-medium">{scriptContent.error}</p>
                    <p className="text-white/40 text-sm mt-2">Please re-upload the script file.</p>
                  </div>
                ) : scriptContent?.content ? (
                  <div className="space-y-4">
                    {/* File Info */}
                    <div className="flex flex-wrap gap-4 p-3 rounded-lg bg-white/5 border border-white/10">
                      <div className="text-xs">
                        <span className="text-white/40">File: </span>
                        <span className="text-white/80 font-mono">{scriptContent.original_filename}</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-white/40">Size: </span>
                        <span className="text-white/80">{scriptContent.content_length.toLocaleString()} characters</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-white/40">Path: </span>
                        <span className="text-white/60 font-mono text-[10px]">{scriptContent.storage_path}</span>
                      </div>
                    </div>

                    {/* Code Display */}
                    <div className="relative">
                      <pre className="p-4 rounded-lg bg-[#1a1a2e] border border-white/10 text-sm text-white/90 font-mono overflow-x-auto whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto">
                        {scriptContent.content}
                      </pre>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(scriptContent.content);
                        }}
                        className="absolute top-2 right-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-white/70 text-xs flex items-center gap-1.5 transition-colors"
                        title="Copy to clipboard"
                      >
                        <FileCode size={12} />
                        Copy
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-20">
                    <p className="text-white/40">No content available</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/10 bg-white/5 flex items-center justify-between flex-shrink-0">
                <p className="text-white/40 text-xs">
                  Downloads will be saved as .txt for mobile compatibility
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={closeScriptViewer}
                    className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white/70 text-sm font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AdminLabPage;
